// Round-trip URL search params <-> typed filter object for /products. Used
// by the route's `validateSearch` and consumed via `useSearch` in the
// products table widget.

export type ProductsSearch = {
  search?: string;
  // Free-text City filter (legacy parity: substring match on `product.city`).
  // Separate from the omni `search` box so consultants can narrow by city
  // without it also matching titles/suppliers.
  city?: string;
  // Free-text Supplier filter (legacy parity: substring match against the
  // `suppliers` jsonb array's `name`). Wire field is `supplierName`.
  supplierName?: string;
  contentCategory?: string[];
  contentSubcategory?: string[];
  catalogId?: string[];
  country?: string[];
  // Budget tier filter (1..4 == $..$$$$). Restricts to Experience categories
  // server-side. See `packages/shared/src/pricing`.
  valueTier?: number[];
  // 'visible' / 'hidden' / undefined (== "All Products" — matches legacy default)
  visibility?: 'visible' | 'hidden';
  // CMS (website) visibility — separate axis from catalog `visibility`.
  // visible == isHiddenOnCms:false, hidden == isHiddenOnCms:true.
  cmsVisibility?: 'visible' | 'hidden';
  // Mini Planner visibility — visible == isHiddenOnMiniPlanner:false.
  miniPlannerVisibility?: 'visible' | 'hidden';
  sortBy?: 'title' | 'price' | 'modifiedOn' | 'stars';
  sortOrder?: 'asc' | 'desc';
  page?: number;
  // Rows per page. Lives in the URL (not local state) so the 50/50 map can
  // mirror the table's exact page. Default 20 when absent.
  pageSize?: number;
  // Custom-region filter. Set from the map's region picker; the products
  // query filters by `customRegionId` so both the table and the map narrow
  // to the region (and the map draws its boundary).
  regionId?: string;
  // Map-viewport bbox (NE/SW corners). Written by the map on user pan/zoom
  // and place-search; the products query filters to products inside it so
  // table + map show only what's in view (legacy parity). Mutually
  // exclusive with `regionId` — setting one clears the other.
  neLat?: number;
  neLng?: number;
  swLat?: number;
  swLng?: number;
};

const PAGE_SIZE_VALUES: ReadonlyArray<number> = [10, 20, 40, 60];

const asStringArray = (v: unknown): string[] | undefined =>
  Array.isArray(v)
    ? v.filter((x): x is string => typeof x === 'string')
    : undefined;

const asFiniteNumber = (v: unknown): number | undefined =>
  typeof v === 'number' && Number.isFinite(v) ? v : undefined;

// Budget tiers from the URL — keep only the four valid integers (1..4). A
// hand-crafted `?valueTier=9` is dropped at the FE boundary.
const asValueTier = (v: unknown): number[] | undefined => {
  if (!Array.isArray(v)) return undefined;
  const tiers = v.filter(
    (x): x is number => typeof x === 'number' && x >= 1 && x <= 4
  );
  return tiers.length > 0 ? tiers : undefined;
};

const asVisibility = (v: unknown): ProductsSearch['visibility'] =>
  v === 'visible' || v === 'hidden' ? v : undefined;

const asCmsVisibility = (v: unknown): ProductsSearch['cmsVisibility'] =>
  v === 'visible' || v === 'hidden' ? v : undefined;

const SORT_BY_VALUES: ReadonlyArray<NonNullable<ProductsSearch['sortBy']>> = [
  'title',
  'price',
  'modifiedOn',
  'stars',
];

const asSortBy = (v: unknown): ProductsSearch['sortBy'] =>
  typeof v === 'string' && (SORT_BY_VALUES as ReadonlyArray<string>).includes(v)
    ? (v as ProductsSearch['sortBy'])
    : undefined;

const asSortOrder = (v: unknown): ProductsSearch['sortOrder'] =>
  v === 'asc' || v === 'desc' ? v : undefined;

export const validateProductsSearch = (
  input: Record<string, unknown>
): ProductsSearch => ({
  search: typeof input.search === 'string' ? input.search : undefined,
  city: typeof input.city === 'string' ? input.city : undefined,
  supplierName:
    typeof input.supplierName === 'string' ? input.supplierName : undefined,
  contentCategory: asStringArray(input.contentCategory),
  contentSubcategory: asStringArray(input.contentSubcategory),
  catalogId: asStringArray(input.catalogId),
  country: asStringArray(input.country),
  valueTier: asValueTier(input.valueTier),
  visibility: asVisibility(input.visibility),
  cmsVisibility: asCmsVisibility(input.cmsVisibility),
  miniPlannerVisibility: asVisibility(input.miniPlannerVisibility),
  sortBy: asSortBy(input.sortBy),
  sortOrder: asSortOrder(input.sortOrder),
  page: typeof input.page === 'number' ? input.page : undefined,
  pageSize:
    typeof input.pageSize === 'number' &&
    PAGE_SIZE_VALUES.includes(input.pageSize)
      ? input.pageSize
      : undefined,
  // Loose UUID check (no version constraint) so hand-crafted URLs that
  // smuggle a non-UUID string get dropped at the FE boundary instead of
  // round-tripping to the BE just to fail its `z.uuid()` gate.
  regionId:
    typeof input.regionId === 'string' &&
    /^[0-9a-f-]{36}$/i.test(input.regionId)
      ? input.regionId
      : undefined,
  neLat: asFiniteNumber(input.neLat),
  neLng: asFiniteNumber(input.neLng),
  swLat: asFiniteNumber(input.swLat),
  swLng: asFiniteNumber(input.swLng),
});
